import { useEffect, useRef, useState } from 'react';
import Pusher from 'pusher-js';
import { backendHost, wsHost } from 'core/api';
import { WS_PATH } from 'config';
import { PUSHER_APP_KEY } from 'constants/pusher';
import { tokenStorage } from 'utils/tokenStorage';
import { handleWebSocketEvent } from 'events/webSocketHandlers';
import { createBroadcastPoller, type BroadcastPoller } from 'services/broadcastPolling';

interface UseWebSocketOptions {
  enabled?: boolean;
}

// Сколько ждём WS-коннект, прежде чем поднять polling-фолбэк (банковские сети
// блокируют исходящий WS — Pusher зависает в 'connecting'/'unavailable').
const WS_CONNECT_TIMEOUT_MS = 5000;
// Как часто в polling-режиме фоном пробуем переподнять WS.
const WS_BACKGROUND_RETRY_MS = 60000;

/**
 * Гибридный транспорт live-уведомлений: WebSocket приоритетен, HTTP-polling —
 * фолбэк. Одна реализация для всех окружений (банк ↔ небанк, без env-веток).
 *
 *   старт → пробуем WS (таймаут 5с)
 *     ├─ connected            → WS-режим, polling заглушен
 *     └─ не поднялся / отвал  → polling-режим + фоновый WS-retry раз в 60с
 *                                  └─ WS поднялся → глушим polling, назад на WS
 *
 * Polling-события приводятся к Pusher-форме и идут в тот же handleWebSocketEvent,
 * поэтому существующие хендлеры работают без изменений.
 */
export const useWebSocket = (
  userId: string | null,
  options: UseWebSocketOptions = {}
): { isConnected: boolean } => {
  const { enabled = true } = options;
  const pusherRef = useRef<Pusher | null>(null);
  const channelRef = useRef<ReturnType<Pusher['subscribe']> | null>(null);
  const pollerRef = useRef<BroadcastPoller | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bgRetryRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !userId) return;

    const apiHost = wsHost?.replace(/^https?:\/\//, '') || '';
    const channelName = `private-user-${userId}`;

    const poller = createBroadcastPoller({ userId });
    pollerRef.current = poller;

    const pusher = new Pusher(PUSHER_APP_KEY, {
      cluster: '',
      wsHost: apiHost,
      httpHost: apiHost,
      wsPath: WS_PATH,
      wsPort: 80,
      wssPort: 443,
      enabledTransports: ['ws', 'wss'],
      // При USE_LOCAL_API backendHost относительный ('/api') — TLS определяем
      // по протоколу страницы, иначе по схеме backendHost.
      forceTLS: backendHost.startsWith('https') || window.location.protocol === 'https:',
      authEndpoint: `${backendHost}/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${tokenStorage.get()}`,
        },
      },
    } as ConstructorParameters<typeof Pusher>[1]);

    pusherRef.current = pusher;

    pusher.connection.bind('state_change', ({ current }: { current: string }) => {
      if (current === 'connected') {
        // WS жив — это основной канал, polling не нужен.
        setIsConnected(true);
        poller.stop();
        if (connectTimeoutRef.current) {
          clearTimeout(connectTimeoutRef.current);
          connectTimeoutRef.current = null;
        }
      } else if (current === 'unavailable' || current === 'failed' || current === 'disconnected') {
        // WS недоступен/отвалился — поднимаем фолбэк (start идемпотентен).
        setIsConnected(false);
        poller.start();
      }
    });

    pusher.connection.bind('error', (err: unknown) => {
      console.warn('[WebSocket] Connection error:', err);
    });

    const channel = pusher.subscribe(channelName);
    channelRef.current = channel;

    channel.bind_global((eventName: string, data: unknown) => {
      handleWebSocketEvent(eventName, data as Record<string, unknown>);
    });

    // Страховка: если за 5с WS так и не поднялся (завис в 'connecting') —
    // не ждём дальше, включаем polling.
    connectTimeoutRef.current = setTimeout(() => {
      if (pusher.connection.state !== 'connected') {
        poller.start();
      }
    }, WS_CONNECT_TIMEOUT_MS);

    // Фоновый WS-retry: пока сидим на polling, периодически пробуем WS снова.
    bgRetryRef.current = setInterval(() => {
      if (pusher.connection.state !== 'connected') {
        pusher.connect();
      }
    }, WS_BACKGROUND_RETRY_MS);

    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }
      if (bgRetryRef.current) {
        clearInterval(bgRetryRef.current);
        bgRetryRef.current = null;
      }
      poller.stop();
      pollerRef.current = null;
      if (channelRef.current) {
        pusher.unsubscribe(channelRef.current.name);
        channelRef.current = null;
      }
      pusher.disconnect();
      pusherRef.current = null;
      setIsConnected(false);
    };
  }, [userId, enabled]);

  return { isConnected };
};
